import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  ChevronRight, PanelLeft, Search, LayoutDashboard, X
} from "lucide-react";
import * as LucideIcons from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "./ui/utils";
import { ScrollArea } from "./ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Link, useLocation } from "react-router-dom";
import { APP_NAVIGATION, NavItem } from "./config/navigation";
import { usePermissions } from "./contexts/PermissionContext";
import { motion, AnimatePresence } from "framer-motion";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  userRole: "Superadmin" | "Admin" | "Teacher" | "Student" | "Client" | "Customer" | null;
}

const resolveIcon = (name: string) => {
  const Icon = (LucideIcons as any)[name];
  return Icon || LayoutDashboard;
};

const springTransition = { duration: 0.2, ease: "easeInOut" } as const;

// --- SubItem Component ---
interface SidebarSubItemProps {
  sub: any;
  onClose: () => void;
  isMobile: boolean;
  isActive: boolean;
}
const SidebarSubItem = React.memo(({ sub, onClose, isMobile, isActive }: SidebarSubItemProps) => {
  const SubIcon = resolveIcon(sub.icon);
  return (
    <Link
      to={sub.path}
      onClick={() => isMobile && onClose()}
      className={cn(
        "flex items-center w-full justify-start rounded-r-xl text-[12.5px] h-[38px] px-4 group/sub whitespace-nowrap ml-1 transition-all duration-150",
        isActive
          ? "text-violet-700 dark:text-violet-300 bg-violet-500/10 dark:bg-violet-500/20 font-semibold border-l-[3px] border-violet-600 dark:border-violet-500 shadow-sm"
          : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-violet-600 dark:hover:text-violet-400 font-medium"
      )}
    >
      <SubIcon className={cn(
        "h-[15px] w-[15px] mr-3 shrink-0 stroke-[2.2px] transition-colors duration-150",
        isActive ? "text-violet-700 dark:text-violet-300" : "text-sidebar-foreground/60 group-hover/sub:text-violet-600 dark:group-hover/sub:text-violet-400"
      )} />
      <span className="truncate">{sub.label}</span>
    </Link>
  );
}, (prev, next) => (
  prev.sub.path === next.sub.path &&
  prev.isMobile === next.isMobile &&
  prev.isActive === next.isActive
));
SidebarSubItem.displayName = "SidebarSubItem";

// --- Main Item Component ---
interface SidebarItemProps {
  item: NavItem;
  isActive: boolean;
  isSubActive: boolean;
  locationPath: string;
  collapsed: boolean;
  isMobile: boolean;
  isExpanded: boolean;
  toggleExpand: (path: string) => void;
  onClose: () => void;
}

const SidebarItem = React.memo(({
  item,
  isActive,
  isSubActive,
  locationPath,
  collapsed,
  isMobile,
  isExpanded,
  toggleExpand,
  onClose
}: SidebarItemProps) => {
  const Icon = resolveIcon(item.icon);
  const hasSubmenu = item.subItems && item.subItems.length > 0;
  const active = isActive || isSubActive;

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (hasSubmenu) {
      e.preventDefault();
      toggleExpand(item.path);
    } else {
      if (isMobile) onClose();
    }
  }, [hasSubmenu, toggleExpand, item.path, isMobile, onClose]);

  const linkContent = (
    <Link
      to={item.path}
      onClick={handleClick}
      className={cn(
        "flex items-center rounded-xl relative group overflow-hidden whitespace-nowrap transition-all duration-200",
        active
          ? "bg-gradient-to-r from-violet-600 to-indigo-600 dark:from-violet-500 dark:to-indigo-500 text-white font-semibold shadow-md shadow-violet-500/20"
          : "text-sidebar-foreground/80 hover:bg-violet-500/10 hover:text-violet-600 dark:hover:bg-violet-500/20 dark:hover:text-violet-400 font-medium",
        (!collapsed || isMobile)
          ? "h-[42px] px-4 mx-2 justify-start"
          : "h-[42px] w-[42px] px-0 justify-center mx-auto"
      )}
    >
      <Icon className={cn(
        "h-[18px] w-[18px] shrink-0 stroke-[2.2px] transition-colors duration-200",
        active ? "text-white" : "text-sidebar-foreground/70 group-hover:text-violet-600 dark:group-hover:text-violet-400"
      )} />

      {(!collapsed || isMobile) && (
        <div className="flex items-center flex-1 ml-3 overflow-hidden">
          <span className="flex-1 text-[13.5px] tracking-wide truncate whitespace-nowrap">
            {item.label}
          </span>
          {hasSubmenu && (
            <ChevronRight className={cn(
              "h-[14px] w-[14px] ml-auto shrink-0 opacity-70 transition-transform duration-200",
              isExpanded && "rotate-90 opacity-100"
            )} />
          )}
        </div>
      )}

      {active && (!collapsed || isMobile) && (
        <motion.div
          layoutId="activeIndicatorMain"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[20px] bg-white rounded-r-full"
        />
      )}
    </Link>
  );

  return (
    <div className="mb-1 w-full">
      {collapsed && !isMobile ? (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            {linkContent}
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={12} className="bg-sidebar-accent border-sidebar-border text-sidebar-foreground text-[12.5px] font-medium shadow-xl px-3 py-1.5 z-[100]">
            {item.label}
          </TooltipContent>
        </Tooltip>
      ) : linkContent}

      <AnimatePresence>
        {hasSubmenu && (!collapsed || isMobile) && isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden ml-[22px] border-l border-sidebar-border/60"
          >
            <div className="space-y-1 pl-2 pb-2 mt-1">
              {item.subItems!.map((sub) => (
                <SidebarSubItem 
                  key={sub.path} 
                  sub={sub} 
                  onClose={onClose} 
                  isMobile={isMobile} 
                  isActive={locationPath === sub.path} 
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}, (prev, next) => (
  prev.isActive === next.isActive &&
  prev.isSubActive === next.isSubActive &&
  prev.collapsed === next.collapsed &&
  prev.isMobile === next.isMobile &&
  prev.isExpanded === next.isExpanded &&
  prev.item === next.item &&
  (prev.isExpanded ? prev.locationPath === next.locationPath : true)
));
SidebarItem.displayName = "SidebarItem";

// --- Main Sidebar Component ---
export function Sidebar({
  isOpen,
  onClose,
  userRole,
}: SidebarProps) {
  const location = useLocation();
  const { allowedSlugs, loading } = usePermissions();
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [expandedMenu, setExpandedMenu] = useState<string | null>(() => {
    return localStorage.getItem("sidebar-expanded");
  });

  const menuItems = useMemo(() => {
    if (loading || allowedSlugs === null) return [];
    let base = [...APP_NAVIGATION];
    if (userRole !== "Superadmin" && !allowedSlugs.has("ALL")) {
      base = base
        .map((item): NavItem | null => {
          const isParentAllowed = allowedSlugs.has(item.slug);
          const filteredSubItems = item.subItems?.filter(sub => allowedSlugs.has(sub.slug));
          if (isParentAllowed || (filteredSubItems && filteredSubItems.length > 0)) {
            return { ...item, subItems: filteredSubItems };
          }
          return null;
        })
        .filter((item): item is NavItem => item !== null);
    }
    if (!searchQuery.trim()) return base;
    const lowerQuery = searchQuery.toLowerCase();
    return base.filter(item =>
      item.label.toLowerCase().includes(lowerQuery) ||
      item.name.toLowerCase().includes(lowerQuery) ||
      item.subItems?.some(sub => sub.label.toLowerCase().includes(lowerQuery))
    );
  }, [userRole, allowedSlugs, loading, searchQuery]);

  useEffect(() => {
    const activeItem = menuItems.find(item =>
      item.path === location.pathname ||
      item.subItems?.some(sub => location.pathname === sub.path)
    );
    if (activeItem && activeItem.subItems) {
      setExpandedMenu(prev => {
        if (prev !== activeItem.path) {
          localStorage.setItem("sidebar-expanded", activeItem.path);
          return activeItem.path;
        }
        return prev;
      });
    }
  }, [location.pathname, menuItems]);

  const toggleExpand = useCallback((path: string) => {
    setCollapsed(false);
    setExpandedMenu(prev => {
      const newVal = prev === path ? null : path;
      if (newVal) {
        localStorage.setItem("sidebar-expanded", newVal);
      } else {
        localStorage.removeItem("sidebar-expanded");
      }
      return newVal;
    });
  }, []);

  return (
    <>
      <AnimatePresence>
        {isOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden print:hidden"
            onClick={onClose}
          />
        )}
      </AnimatePresence>

      <motion.aside
        initial={false}
        animate={{
          width: collapsed && !isMobile ? 80 : 256,
          x: isOpen || !isMobile ? 0 : -256
        }}
        transition={springTransition}
        className={cn(
          "fixed lg:sticky top-0 left-0 h-screen bg-sidebar border-r border-sidebar-border z-50 flex flex-col shadow-xl lg:shadow-none print:hidden"
        )}
      >
        <div className="h-16 border-b border-sidebar-border flex items-center justify-between px-4 lg:hidden">
          <span className="font-semibold text-[15px] tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-indigo-600 dark:from-violet-400 dark:to-indigo-400">
            XEROALPHA
          </span>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-xl h-8 w-8">
            <X className="h-[18px] w-[18px] stroke-[2.5px]" />
          </Button>
        </div>

        <div className={cn("hidden lg:flex items-center transition-all duration-200", collapsed ? "px-0 justify-center h-[72px]" : "px-4 h-[84px]")}>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={springTransition}
              className="flex-1 mr-3"
            >
              <div className="relative flex items-center bg-sidebar-accent/50 border border-sidebar-border/60 rounded-xl px-3 h-[38px] transition-all hover:bg-sidebar-accent focus-within:bg-sidebar-accent focus-within:ring-2 focus-within:ring-violet-500/20 focus-within:border-violet-500/30">
                <Search className="h-[15px] w-[15px] text-sidebar-foreground/50 mr-2 shrink-0 stroke-[2.5px]" />
                <input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-transparent border-none focus:outline-none text-[13px] text-sidebar-foreground placeholder:text-sidebar-foreground/40 w-full font-medium"
                />
              </div>
            </motion.div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className={cn(
              "shrink-0 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-xl flex items-center justify-center transition-all duration-200",
              collapsed ? "h-[42px] w-[42px] mx-auto" : "h-[38px] w-[38px]"
            )}
          >
            <PanelLeft className="h-[18px] w-[18px] stroke-[2.2px]" />
          </Button>
        </div>

        <ScrollArea className="flex-1 overflow-y-auto px-3 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
          <TooltipProvider>
            <nav className="space-y-[4px] pb-24 pt-2">
              {loading ? (
                <div className="space-y-2 px-1 pt-1 animate-pulse">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className={cn("rounded-xl bg-sidebar-accent/50", collapsed ? "h-[42px] w-[42px] mx-auto" : "h-[42px] w-full")} />
                  ))}
                </div>
              ) : (
                menuItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  const isSubActive = item.subItems?.some(sub => location.pathname === sub.path) || false;
                  
                  return (
                    <SidebarItem
                      key={item.path}
                      item={item}
                      isActive={isActive}
                      isSubActive={isSubActive}
                      locationPath={location.pathname}
                      collapsed={collapsed}
                      isMobile={isMobile}
                      isExpanded={expandedMenu === item.path}
                      toggleExpand={toggleExpand}
                      onClose={onClose}
                    />
                  );
                })
              )}
            </nav>
          </TooltipProvider>
        </ScrollArea>

        <div className="p-4 border-t border-sidebar-border text-[10px] text-sidebar-foreground/40 font-medium text-center uppercase tracking-widest bg-transparent">
          {collapsed && !isMobile ? "XA" : "© 2026 XEROALPHA"}
        </div>
      </motion.aside>
    </>
  );
}